import { create } from 'zustand';
import type { AssistantAnswer, AssistantPageContext, CallJoinInfo, PresenceStatus } from '@tradeoutlet/shared';
import { addTyper, pruneTypers, removeTyper, TYPING_TTL_MS } from '@tradeoutlet/shared';

export interface ToastAction { label: string; run: () => void }

export interface IncomingCall {
  call_id: string;
  from_user_id: string;
  from_name: string;
  media: 'audio' | 'video';
  conversation_id: string | null;
  received_at: number;
}

export interface AssistantTurn { id: number; question: string; status: 'loading' | 'done' | 'error'; result?: AssistantAnswer; error?: string }
export type AssistantOrigin = 'panel' | 'page' | 'slash';

interface UiState {
  workspaceId: string | null;
  setWorkspace: (id: string) => void;
  presence: Record<string, PresenceStatus>;
  setPresence: (entries: { user_id: string; status: PresenceStatus }[]) => void;
  /** conversationId -> userId -> expiresAt */
  typing: Record<string, Record<string, number>>;
  /** Key: typingKey(conversation, thread root) from @tradeoutlet/shared. */
  setTyping: (key: string, userId: string, ttlMs?: number) => void;
  /** t657u: their message arrived, so they're no longer typing there. */
  clearTyping: (key: string, userId: string) => void;
  pruneTyping: () => void;
  incomingCall: IncomingCall | null;
  setIncomingCall: (c: IncomingCall | null) => void;
  activeCall: CallJoinInfo | null;
  setActiveCall: (c: CallJoinInfo | null) => void;
  threadRootId: string | null;
  openThread: (id: string | null) => void;
  taskPanelId: string | null;
  openTask: (id: string | null) => void;
  /** t171u: the "Report a bug or suggest a feature" dialog, opened from Help. */
  feedbackOpen: boolean;
  openFeedback: (v: boolean) => void;
  /** UI review: the person drawer opened from any avatar or name. */
  personId: string | null;
  openPerson: (id: string | null) => void;
  /** Rooms (#54): the New room modal, opened from the sidebar, the Chat list and the call menu. */
  newRoomOpen: boolean;
  setNewRoomOpen: (v: boolean) => void;
  /** UI review: the ⌘K command palette. */
  paletteOpen: boolean;
  setPaletteOpen: (v: boolean) => void;
  /** Help Centre side panel: an article key ("schedule/build-the-rota"), or 'route' for the current page's article. */
  helpPanel: string | null;
  openHelp: (key?: string) => void;
  closeHelp: () => void;
  /** Admins: show guides for every role, not just their own (persisted). */
  helpShowAll: boolean;
  setHelpShowAll: (v: boolean) => void;
  theme: 'light' | 'dark';
  toggleTheme: () => void;
  /** Top-bar notifications panel: closed, or open on All or Unread (Home's "Notifications" stat opens Unread). */
  notificationsPanel: null | 'all' | 'unread';
  setNotificationsPanel: (v: null | 'all' | 'unread') => void;
  /** AI assistant (#8): side panel state and this tab's Q&A history (not persisted). */
  assistantOpen: boolean;
  assistantPending: { question: string; origin: AssistantOrigin } | null;
  assistantTurns: AssistantTurn[];
  openAssistant: (question?: string, origin?: AssistantOrigin) => void;
  closeAssistant: () => void;
  takeAssistantPending: () => { question: string; origin: AssistantOrigin } | null;
  setAssistantTurn: (t: AssistantTurn) => void;
  clearAssistant: () => void;
  /** #52: what the current page tells the assistant (the Schedule sets its week, building and filters). */
  assistantPage: AssistantPageContext['schedule'] | null;
  setAssistantPage: (p: AssistantPageContext['schedule'] | null) => void;
  toasts: { id: number; text: string; tone: 'info' | 'error' | 'success'; action?: ToastAction }[];
  /** `action` adds one button (e.g. Undo); pressing it runs the action and dismisses the toast. */
  toast: (text: string, tone?: 'info' | 'error' | 'success', action?: ToastAction) => void;
  dismissToast: (id: number) => void;
}

let toastId = 0;
const savedTheme = (typeof localStorage !== 'undefined' && localStorage.getItem('collab.theme')) as 'light' | 'dark' | null;

export const useUi = create<UiState>((set, get) => ({
  workspaceId: typeof localStorage !== 'undefined' ? localStorage.getItem('collab.workspace') : null,
  setWorkspace: id => { localStorage.setItem('collab.workspace', id); set({ workspaceId: id }); },
  presence: {},
  setPresence: entries => set(s => ({ presence: { ...s.presence, ...Object.fromEntries(entries.map(e => [e.user_id, e.status])) } })),
  typing: {},
  setTyping: (key, uid, ttl = TYPING_TTL_MS) => set(s => ({ typing: addTyper(s.typing, key, uid, Date.now(), ttl) })),
  clearTyping: (key, uid) => { const cur = get().typing, next = removeTyper(cur, key, uid); if (next !== cur) set({ typing: next }); },
  pruneTyping: () => { const cur = get().typing, next = pruneTypers(cur); if (next !== cur) set({ typing: next }); },
  incomingCall: null,
  setIncomingCall: c => set({ incomingCall: c }),
  activeCall: null,
  setActiveCall: c => set({ activeCall: c }),
  threadRootId: null,
  openThread: id => set({ threadRootId: id }),
  taskPanelId: null,
  openTask: id => set({ taskPanelId: id }),
  feedbackOpen: false,
  openFeedback: v => set({ feedbackOpen: v }),
  personId: null,
  openPerson: id => set({ personId: id }),
  newRoomOpen: false,
  setNewRoomOpen: v => set({ newRoomOpen: v }),
  paletteOpen: false,
  setPaletteOpen: v => set({ paletteOpen: v }),
  helpPanel: null,
  openHelp: key => set({ helpPanel: key ?? 'route', assistantOpen: false }),
  closeHelp: () => set({ helpPanel: null }),
  helpShowAll: typeof localStorage !== 'undefined' && localStorage.getItem('collab.help.showAll') === '1',
  setHelpShowAll: v => { localStorage.setItem('collab.help.showAll', v ? '1' : '0'); set({ helpShowAll: v }); },
  theme: savedTheme ?? 'light',
  toggleTheme: () => set(s => {
    const theme = s.theme === 'light' ? 'dark' : 'light';
    localStorage.setItem('collab.theme', theme);
    return { theme };
  }),
  notificationsPanel: null,
  setNotificationsPanel: v => set({ notificationsPanel: v }),
  assistantOpen: false,
  assistantPending: null,
  assistantTurns: [],
  openAssistant: (question, origin = 'panel') => set({ assistantOpen: true, helpPanel: null, assistantPending: question ? { question, origin } : null }),
  closeAssistant: () => set({ assistantOpen: false }),
  takeAssistantPending: () => { const p = get().assistantPending; if (p) set({ assistantPending: null }); return p; },
  setAssistantTurn: t => set(s => ({ assistantTurns: s.assistantTurns.some(x => x.id === t.id) ? s.assistantTurns.map(x => (x.id === t.id ? t : x)) : [...s.assistantTurns, t].slice(-20) })),
  clearAssistant: () => set({ assistantTurns: [] }),
  assistantPage: null,
  setAssistantPage: p => set({ assistantPage: p }),
  toasts: [],
  toast: (text, tone = 'info', action) => {
    const id = ++toastId;
    set(s => ({ toasts: [...s.toasts, { id, text, tone, action }] }));
    setTimeout(() => get().dismissToast(id), 5000);
  },
  dismissToast: id => set(s => ({ toasts: s.toasts.filter(t => t.id !== id) })),
}));
